import defer from "./defer";
import Cluster from "./Cluster";
import Marker from "./Marker";
import MapControlBase from "./MapControlBase";

/**
 * map control class for add marker, cluster, window info and move map
 */
export class MapControl extends MapControlBase {
  // Map of marker, key = storeCode
  _marker = new Map();

  // Map of window info, key = storeCode
  _infoWindow = new Map();

  // last window open
  _infoWindowOpen = null;

  // icon store toogle
  _iconMarkerOver = null;

  // marker position of user
  _markerPosition = null;

  // cluster instance
  _cluster = null;

  /**
   * add the marker list on map and create window info for stores
   * @param {array} list of stores
   * @param {function} function for create window info
   * @param {string} label for marker icon
   *
   * @return {Promise}
   */
  addMarkerOnMap = (list, createInfoWindow) =>
    new Promise(resolve => {
      // remove marker on map
      this.removeMarker();
      // create marker
      list.forEach(d => {
        const lat = d.lat;
        const lng = d.lng;
        // key for marker
        const storeCode = d.title;
        let type;
        switch (true) {
          case d.online === true && d.automate === false:
            type = "online";
            break;
          case d.online === false && d.automate === true:
            type = "automate";
            break;
          case d.online === true && d.automate === true:
            type = "all";
            break;
          default:
            type = "all";
            break;
        }

        if (lat && lng) {
          const position = { lat: Number(lat), lng: Number(lng) };
          // create marker
          this.createMarker(storeCode, position, type);

          // create info window, callback in params
          defer(() => {
            this.createInfoWindow(storeCode, createInfoWindow(d), position);
          });
        }
      });
      // create cluster
      this._cluster = new Cluster(this._map, this.getMarkerArray());
      // redifined zindex for markerPosition if exist
      if (this._markerPosition !== null) {
        this._markerPosition.setZIndex(this._marker.size + 1);
      }
      return resolve();
    });

  /**
   * move to marker
   * @param {string} key of marker
   */
  moveToMarker = key => {
    const m = this._marker.get(key);
    const zoom = this.getZoom();
    if (m) {
      if (zoom !== 15) {
        this.setZoom(15);
      }
      // move center to position
      this._map.panTo(m.position);
      window.google.maps.event.trigger(m.marker, "click", {});
    }
  };

  /**
   * add marker for indicate position of user
   * @param {object} latitude, longitude
   */
  addMarkerPosition = ({ latitude, longitude }) => {
    this.removeMarkerPosition();
    this._markerPosition = new window.google.maps.Marker({
      position: { lat: latitude, lng: longitude },
      map: this._map,
      // add marker position on the top of all markers
      zIndex: this._marker.size + 1
    });
  };

  /**
   * remove the marker of position user
   */
  removeMarkerPosition = () => {
    if (this._markerPosition !== null) {
      this._markerPosition.setMap(null);
    }
  };

  /**
   * remove marker on map
   */
  removeMarker = () => {
    if (this._marker.size === 0) return;

    this._marker.forEach(marker => {
      const { eventListener } = marker;
      // clear listener of marker
      eventListener.forEach(e => window.google.maps.event.removeListener(e));
      // remove to map
      marker.marker.setMap(null);
    });
    // clear Map
    this._marker.clear();

    // remove marker on cluster
    this._cluster.clearMarkers();
    // delete cluster instance
    delete this._cluster;
  };

  /**
   * create marker and add to map
   *
   * @param {string} key of marker
   * @param {object} latitude, longitude
   * @param {string} label for marker icon
   */
  createMarker = (key, position, label) => {
    const marker = new Marker(this._map, position, label);
    this._marker.set(key, marker);
  };

  /**
   * close window info on clic to another
   */
  _closeInfoWindowOpen = (infoWindow, marker) => {
    if (this._infoWindowOpen !== null && this._infoWindowOpen !== infoWindow) {
      this._infoWindowOpen.close();
      this._markerOpen.setVisible(true);
    }
    this._infoWindowOpen = infoWindow;
    this._markerOpen = marker;
  };

  /**
   * return array with the markers
   */
  getMarkerArray() {
    const k = Array.from(this._marker.keys());
    return k.map(m => this._marker.get(m).marker);
  }

  /**
   * create windo info and add to map
   * @param {string} key of marker
   * @param {string} content of window
   * @param {object} latitude, longitude
   */
  createInfoWindow = (key, content, position) => {
    this._infoWindow.set(
      key,
      new window.google.maps.InfoWindow({
        content: content,
        position: position
      })
    );

    // sho marker on click close
    google.maps.event.addListener(this._infoWindow.get(key), "closeclick", () =>
      this._markerOpen.setVisible(true)
    );
    // attach info window to marker
    this._marker
      .get(key)
      .linkInfoWindow(this._infoWindow.get(key), this._closeInfoWindowOpen);
  };
}

export default MapControl;
